import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseEntity } from '@base/entities/BaseEntities';
import { EnumRequestProgressTracking } from "../framework/sysenums.enum";
import { Utils } from '../helpers/Utils';
import { AppConfigService } from '../../configuration/app.config.service';

@Injectable()
export class APIGatewayService {
    constructor(private http: HttpClient,
        private appConfigService: AppConfigService) {
    }

    //Controller based actions
    //********************************
    public ExecuteGetAction(sController: string, sAction: string, QueryParams?: any, TrackProgress?: EnumRequestProgressTracking, ResponseType?: string, Headers?: { [header: string] : string | string[] }, Context?: HttpContext): Observable<any> {
        return this.ExecuteGetRequest(sController + '/' + sAction, QueryParams, TrackProgress, ResponseType, Headers, Context);
    }

    public ExecutePostAction(sController: string, sAction: string, Entity: BaseEntity | BaseEntity[], QueryParams?: any, TrackProgress?: EnumRequestProgressTracking, Headers?: { [header: string] : string | string[] }, Context?: HttpContext): Observable<any> {
        return this.ExecutePostRequest(sController + '/' + sAction, Entity, QueryParams, TrackProgress, Headers, Context);
    }

    public ExecuteDeleteAction(sController: string, sAction: string, QueryParams?: any, TrackProgress?: EnumRequestProgressTracking, Headers?: { [header: string] : string | string[] }, Context?: HttpContext): Observable<any> {
        return this.ExecuteDeleteRequest(sController + '/' + sAction, QueryParams, TrackProgress, Headers, Context);
    }

    //*******************************
    //HTTP-Level requests
    //*******************************
    private ExecutePostRequest(url: string, data: any, QueryParams: any, TrackProgress?: EnumRequestProgressTracking, Headers?: { [header: string] : string | string[] }, Context?: HttpContext): Observable<any> {
        return this.http.post(this.GetAPIURL() + url, Utils.CloneObject(data), this.GetParams(QueryParams, TrackProgress, undefined, Headers, Context)); //Notice the cloning, to avoid changing the actual fields when parsing the body
    }

    private ExecuteGetRequest(url: string, QueryParams?: any, TrackProgress?: EnumRequestProgressTracking, ResponseType?: string, Headers?: { [header: string] : string | string[] }, Context?: HttpContext): Observable<any> {
        return this.http.get(this.GetAPIURL() + url, this.GetParams(QueryParams, TrackProgress, ResponseType, Headers, Context));
    }

    private ExecuteDeleteRequest(url: string, QueryParams?: any, TrackProgress?: EnumRequestProgressTracking, Headers?: { [header: string] : string | string[] }, Context?: HttpContext): Observable<any> {
        return this.http.delete(this.GetAPIURL() + url, this.GetParams(QueryParams, TrackProgress, undefined, Headers, Context));
    }

    //Return request parameters
    private GetParams(QueryParams: any, TrackProgress?: EnumRequestProgressTracking, ResponseType?: string, Headers?: { [header: string] : string | string[] }, Context?: HttpContext) {
        let ret =
        {
            params: this.PrepareQueryParams(QueryParams),
            headers: this.GetHeaders(TrackProgress, Headers),
            reportProgress: (TrackProgress != undefined),
            context: Context
        };

        if (ResponseType) {
            (ret as any).responseType = ResponseType;
        }

        return ret;
    }

    private GetHeaders(TrackProgress?: EnumRequestProgressTracking, Headers?: { [header: string] : string | string[] }): { [header: string] : string | string[] } {
        let headers = Headers;
        if (TrackProgress) headers = { ...{ 'm4u-trackprogress': TrackProgress }, ...headers };
        return headers;
    }


    private PrepareQueryParams(QueryParams: any): any {

        //Step 1 - pre-process        
        var params = this.PrepareQueryParamsInternal(QueryParams);


        //Step 2 - flatten/spread
        if (params != undefined) {
            Object.keys(params).forEach(key => {
                var value = params[key];
                if (value instanceof Object && !(value instanceof Array)) {
                    Object.keys(value).forEach(childKey => {
                        var valueChild = value[childKey];
                        params[childKey] = valueChild;
                    });
                    delete params[key];
                }
            });
        }

        //Step 3 - format dates
        params = this.PrepareQueryParamsInternal(params);
        return params;
    }

    //Prepare query parameters - format dates and remove nulls
    private PrepareQueryParamsInternal(QueryParams: any): any {
        if (QueryParams === undefined) return undefined;
        Object.keys(QueryParams).forEach(key => {
            var value = QueryParams[key];
            if (value === undefined || value === null || value == "") delete QueryParams[key];
            if (value instanceof Date) QueryParams[key] = this.ConvertToServerDate(value);
        });
        return QueryParams;
    }

    //*******************************


    //Formats value for sending to the server, without time zone
    private ConvertToServerDate(value: Date): string {
        var D = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds()));
        var S = D.toISOString();
        if (S.endsWith("Z")) S = S.substring(0, S.length - 1);
        return S;
    }


    //Return the base URL for the API
    public GetAPIURL(): string {
        if (this.appConfigService.M4UConfig.standaloneApplicationApiUrl) {
            return this.appConfigService.M4UConfig.standaloneApplicationApiUrl;
        }

            //Debug overrides
            //Use port 4444 for development for HTTP, 44333 for HTTPS
            const port: string = location.port == "4200" || location.port == "4201"
                ? (location.protocol == "http:" ? "4444" : "44333")
                : location.port;

            const formattedPort = port && `:${port}` || '';
            const baseUrl = this.appConfigService.M4UConfig.deploymentApplicationFolder;
            const formattedBaseUrl = baseUrl && `${baseUrl.replace('/', '')}/` || '';

            //Combine
            return `${location.protocol}//${location.hostname}${formattedPort}/${formattedBaseUrl}api/`;
        // }
    }
}

